import { Address } from 'viem'

interface EulerPositionLike {
  subAccountId: number
}

interface EulerDebtVaultLike {
  liabilityVaultAddress: string
}

interface EulerCollateralLike {
  vaultAddress: string
}

interface EulerPositionWithDebtLike extends EulerPositionLike {
  debtVaults: EulerDebtVaultLike[]
  collateral: EulerCollateralLike[]
}

interface ShouldAutoSelectNewEulerPositionParams {
  selectedMarket: 'hypurrFi' | 'euler'
  isNewEulerPosition: boolean
  isPositionsLoading: boolean
  eulerSubAccountId: number
  relevantEulerPositions: EulerPositionLike[]
}

interface GetRelevantEulerPositionsParams<TPosition extends EulerPositionWithDebtLike> {
  allEulerPositions: TPosition[]
  activeEulerVaultAddresses: string[]
  activeEulerCollateralVaultAddresses?: string[]
}

interface GetActiveEulerClusterParams {
  activeCardIsEuler: boolean
  activeCardVaultAddress?: string
  isPrimeVault: (address: string) => boolean
}

interface GetLiveCardMarketParams {
  solverType?: string
  activeCardCluster: 'prime' | 'yield' | null
}

interface AddressLike {
  address: string
}

interface ResolveConfiguredEulerVaultParams<TVault extends AddressLike> {
  selectedEulerVault: TVault | null
  availableEulerVaults: TVault[]
  configuredVaultAddress?: string
}

interface ResolveConfiguredBorrowTokenParams<TToken extends AddressLike> {
  selectedBorrowToken: TToken | null
  availableBorrowTokens: TToken[]
  configuredBorrowTokenAddress?: string
}

interface StoredEulerMarketSelection {
  subAccountId: number
  isNewPosition: boolean
  selectedVaultAddress: string | null
}

interface ResolveStoredEulerMarketSelectionParams<TVault extends AddressLike> {
  savedSelection: StoredEulerMarketSelection | null
  availableEulerVaults: TVault[]
}

interface GetPooledDebtTokenAddressParams {
  isPooledMarket: boolean
  borrowTokenAddress?: string
  hypurrFiDebtTokenByBorrowToken: Record<string, string>
}

interface GetRequiredConfiguredSelectionErrorParams {
  mode: 'borrow' | 'spend'
  market: 'hypurrFi' | 'euler'
  hasSelectedAsset: boolean
  hasBorrowToken: boolean
  hasEulerVault: boolean
  isBorrowTokenFixedByPosition?: boolean
}

interface GetUnsupportedDebtCheckSubAccountIdParams {
  selectedMarket: 'hypurrFi' | 'euler'
  isNewEulerPosition: boolean
  selectedEulerSubAccountId: number
}

interface GetPooledBorrowEModeMismatchParams {
  userEModeCategoryId: number
  userEModeCategoryLabel?: string
  borrowAssetEModeCategoryId?: number
  borrowAssetEModeCategoryLabel?: string
}

interface GetEulerDelegationSetupActionParams {
  isSetupComplete: boolean
  isAuthorizationLoaded: boolean
}

export function getEulerDelegationSetupAction({
  isSetupComplete,
  isAuthorizationLoaded,
}: GetEulerDelegationSetupActionParams): 'wait' | 'approve' | 'skip' {
  if (!isAuthorizationLoaded) {
    return 'wait'
  }

  return isSetupComplete ? 'skip' : 'approve'
}

export function shouldAutoSelectNewEulerPosition({
  selectedMarket,
  isNewEulerPosition,
  isPositionsLoading,
  eulerSubAccountId,
  relevantEulerPositions,
}: ShouldAutoSelectNewEulerPositionParams): boolean {
  if (selectedMarket !== 'euler' || isNewEulerPosition || isPositionsLoading) {
    return false
  }

  return !relevantEulerPositions.some((position) => position.subAccountId === eulerSubAccountId)
}

export function getRelevantEulerPositions<TPosition extends EulerPositionWithDebtLike>({
  allEulerPositions,
  activeEulerVaultAddresses,
  activeEulerCollateralVaultAddresses,
}: GetRelevantEulerPositionsParams<TPosition>): TPosition[] {
  if (activeEulerVaultAddresses.length === 0) return allEulerPositions

  const activeEulerVaultSet = new Set(activeEulerVaultAddresses.map((address) => address.toLowerCase()))
  const activeEulerCollateralVaultSet = new Set(
    (activeEulerCollateralVaultAddresses ?? []).map((address) => address.toLowerCase()),
  )

  return allEulerPositions.filter(
    (position) =>
      position.debtVaults.some((debt) => activeEulerVaultSet.has(debt.liabilityVaultAddress.toLowerCase())) ||
      (position.debtVaults.length === 0 &&
        (activeEulerCollateralVaultSet.size === 0 ||
          position.collateral.some((collateral) =>
            activeEulerCollateralVaultSet.has(collateral.vaultAddress.toLowerCase()),
          ))),
  )
}

export function getActiveEulerCluster({
  activeCardIsEuler,
  activeCardVaultAddress,
  isPrimeVault,
}: GetActiveEulerClusterParams): 'prime' | 'yield' | null {
  if (!activeCardIsEuler || !activeCardVaultAddress) {
    return null
  }

  return isPrimeVault(activeCardVaultAddress) ? 'prime' : 'yield'
}

export function getLiveCardMode(solverType?: string): 'borrow' | 'spend' | null {
  if (solverType === 'hypurrFi' || solverType === 'euler') {
    return 'borrow'
  }

  if (solverType === 'hypurrFi-withdraw' || solverType === 'euler-withdraw') {
    return 'spend'
  }

  return null
}

export function getLiveCardMarket({
  solverType,
  activeCardCluster,
}: GetLiveCardMarketParams): 'hypurrFi' | 'prime' | 'yield' | null {
  if (solverType === 'hypurrFi' || solverType === 'hypurrFi-withdraw') {
    return 'hypurrFi'
  }

  if ((solverType === 'euler' || solverType === 'euler-withdraw') && activeCardCluster) {
    return activeCardCluster
  }

  return null
}

export function getCurrentConfigMarketLabel(liveCardMarket: 'hypurrFi' | 'prime' | 'yield' | null): string {
  if (liveCardMarket === 'hypurrFi') return 'Pooled'
  if (liveCardMarket === 'prime') return 'Prime'
  if (liveCardMarket === 'yield') return 'Yield'
  return 'Unknown'
}

export function getPooledDebtTokenAddress({
  isPooledMarket,
  borrowTokenAddress,
  hypurrFiDebtTokenByBorrowToken,
}: GetPooledDebtTokenAddressParams): string | undefined {
  if (!isPooledMarket || !borrowTokenAddress) {
    return undefined
  }

  return hypurrFiDebtTokenByBorrowToken[borrowTokenAddress.toLowerCase()]
}

export function getRequiredConfiguredSelectionError({
  mode,
  market,
  hasSelectedAsset,
  hasBorrowToken,
  hasEulerVault,
  isBorrowTokenFixedByPosition = false,
}: GetRequiredConfiguredSelectionErrorParams): string | null {
  if (mode === 'spend' && !hasSelectedAsset) {
    return 'Please select a collateral asset first'
  }

  if (market === 'euler' && mode === 'borrow' && !isBorrowTokenFixedByPosition && !hasEulerVault) {
    return 'Please select an Euler vault first'
  }

  if (market === 'hypurrFi' && mode === 'borrow' && !hasBorrowToken) {
    return 'Please select a borrow token first'
  }

  return null
}

export function getSpendCollateralEmptyStateMessage(supportedLabels: string[]): string {
  if (supportedLabels.length === 0) {
    return 'No collateral available. Deposit assets first to enable spending.'
  }

  return `No collateral available. Deposit supported collateral first to enable spending. Supported: ${supportedLabels.join(', ')}.`
}

export function getSpendCollateralSupportedIconDisplay(supportedLabels: string[]): {
  visibleLabels: string[]
  remainingCount: number
} {
  const visibleLabels = supportedLabels.slice(0, 5)
  const remainingCount = Math.max(0, supportedLabels.length - visibleLabels.length)

  return {
    visibleLabels,
    remainingCount,
  }
}

export function getEulerCollateralState(params: {
  collateral: Array<{ vaultAddress: string; depositAmountUSD: number }>
  enabledVaults: string[]
  controllerMarketKey?: string
  resolveMarket: (vaultAddress: Address) => { key?: string } | undefined
}): {
  enabledCollateralUSD: number
  savingsUSD: number
  eligibleCollateralItems: Array<{ vaultAddress: string; depositAmountUSD: number }>
  hasAnyDeposit: boolean
  hasEnabledCollateral: boolean
  hasSavings: boolean
  savingsOnly: boolean
} {
  const enabledSet = new Set(params.enabledVaults)
  let enabledCollateralUSD = 0
  let savingsUSD = 0
  const eligibleCollateralItems: Array<{ vaultAddress: string; depositAmountUSD: number }> = []

  for (const item of params.collateral) {
    const isEnabled = enabledSet.has(item.vaultAddress.toLowerCase())
    if (!isEnabled) {
      savingsUSD += item.depositAmountUSD
      continue
    }

    if (!params.controllerMarketKey) {
      enabledCollateralUSD += item.depositAmountUSD
      eligibleCollateralItems.push(item)
      continue
    }

    const vaultMarketKey = params.resolveMarket(item.vaultAddress as Address)?.key
    if (vaultMarketKey === params.controllerMarketKey) {
      enabledCollateralUSD += item.depositAmountUSD
      eligibleCollateralItems.push(item)
    } else {
      savingsUSD += item.depositAmountUSD
    }
  }

  const hasAnyDeposit = params.collateral.length > 0
  const hasEnabledCollateral = enabledCollateralUSD > 0
  const hasSavings = savingsUSD > 0
  const savingsOnly = hasAnyDeposit && !hasEnabledCollateral && hasSavings

  return {
    enabledCollateralUSD,
    savingsUSD,
    eligibleCollateralItems,
    hasAnyDeposit,
    hasEnabledCollateral,
    hasSavings,
    savingsOnly,
  }
}

export function getUnsupportedDebtCheckSubAccountId({
  selectedMarket,
  isNewEulerPosition,
  selectedEulerSubAccountId,
}: GetUnsupportedDebtCheckSubAccountIdParams): number | undefined {
  if (selectedMarket === 'euler' && !isNewEulerPosition) {
    return selectedEulerSubAccountId
  }

  return undefined
}

export function getPooledBorrowEModeMismatchMessage({
  userEModeCategoryId,
  userEModeCategoryLabel,
  borrowAssetEModeCategoryId,
  borrowAssetEModeCategoryLabel,
}: GetPooledBorrowEModeMismatchParams): string | null {
  if (userEModeCategoryId === 0 || borrowAssetEModeCategoryId == null) {
    return null
  }

  if (userEModeCategoryId === borrowAssetEModeCategoryId) {
    return null
  }

  const userLabel = userEModeCategoryLabel ?? `category ${userEModeCategoryId}`
  const assetLabel = borrowAssetEModeCategoryLabel ?? `category ${borrowAssetEModeCategoryId}`

  return `Activation unavailable. Your account is in ${userLabel} eMode, but this pooled borrow asset uses ${assetLabel}. Disable eMode or choose a compatible borrow asset first.`
}

export function resolveConfiguredEulerVault<TVault extends AddressLike>({
  selectedEulerVault,
  availableEulerVaults,
  configuredVaultAddress,
}: ResolveConfiguredEulerVaultParams<TVault>): TVault | null {
  if (selectedEulerVault) {
    return selectedEulerVault
  }

  if (!configuredVaultAddress) {
    return null
  }

  return (
    availableEulerVaults.find((vault) => vault.address.toLowerCase() === configuredVaultAddress.toLowerCase()) ?? null
  )
}

export function resolveConfiguredBorrowToken<TToken extends AddressLike>({
  selectedBorrowToken,
  availableBorrowTokens,
  configuredBorrowTokenAddress,
}: ResolveConfiguredBorrowTokenParams<TToken>): TToken | null {
  if (selectedBorrowToken) {
    return selectedBorrowToken
  }

  if (!configuredBorrowTokenAddress) {
    return null
  }

  return (
    availableBorrowTokens.find(
      (token) => token.address.toLowerCase() === configuredBorrowTokenAddress.toLowerCase(),
    ) ?? null
  )
}

export function resolveStoredEulerMarketSelection<TVault extends AddressLike>({
  savedSelection,
  availableEulerVaults,
}: ResolveStoredEulerMarketSelectionParams<TVault>): {
  subAccountId: number
  isNewPosition: boolean
  selectedVault: TVault | null
} | null {
  if (!savedSelection) {
    return null
  }

  const selectedVault = savedSelection.selectedVaultAddress
    ? availableEulerVaults.find((vault) => vault.address.toLowerCase() === savedSelection.selectedVaultAddress!.toLowerCase()) ?? null
    : null

  return {
    subAccountId: savedSelection.subAccountId,
    isNewPosition: savedSelection.isNewPosition,
    selectedVault,
  }
}
